import {
  mutationOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import axios from 'axios'
import moment from 'moment-timezone'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import {
  CreateBudgetToastMessage,
  CreateCreditCardVerificationSuccessMessage,
  NewVerificationCodeSentToastMessage,
} from 'src/app/Toast/Toast'
import {
  CreditCardListModel,
  CreditCardStatus,
} from 'src/models/creditCard.models'
import { closeModal } from 'src/navigation/Lnk'
import { invalidateCanIssueVirtualCardsStatistics } from 'src/queries/creditCards/creditCardWriteStatistics'
import {
  CreateCreditCardBody,
  CreditCardWriteResponse,
  VerifyCreditCardBody,
} from 'src/queries/creditCards/network/creditCardWrites.models'
import {
  postCreditCard,
  postStartCreditCardVerification,
  postVerifyCreditCard,
} from 'src/queries/creditCards/network/creditCardWrites.network'
import { QueryError } from 'src/queries/network.utils'
import { creditCardsQueryKeys } from 'src/queries/queries.constants'
import {
  getCreditCardsStatisticsActiveBudgetCount,
  getCreditCardsStatisticsActiveVcnCount,
  getCreditCardsStatisticsBudgetCount,
  getCreditCardsStatisticsVcnCount,
  POST_CREDIT_CARD_FAILURE,
  POST_CREDIT_CARD_INIT,
  POST_CREDIT_CARD_START_VERIFICATION_FAILURE,
  POST_CREDIT_CARD_START_VERIFICATION_INIT,
  POST_CREDIT_CARD_START_VERIFICATION_SUCCESS,
  POST_CREDIT_CARD_SUCCESS,
  POST_CREDIT_CARD_VERIFY_FAILURE,
  POST_CREDIT_CARD_VERIFY_INIT,
  POST_CREDIT_CARD_VERIFY_SUCCESS,
} from 'src/redux/actions/data/creditCards.actions'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { BackendErrorPayload } from 'src/utils/error.utils'

/**
 * `PUT /creditcards/{id}` lives in its own leaf module so that consumers which
 * only update a card do not pull this module's Toast / Redux-action import
 * graph — and with it the store-initialization import cycle — into theirs. See
 * `creditCardUpdate.queries.ts`. Re-exported here so existing call sites that
 * import it from this module keep resolving.
 */
export {
  creditCardUpdateMutations,
  useUpdateCreditCard,
} from 'src/queries/creditCards/creditCardUpdate.queries'

// Fresh timestamp per call (the legacy create thunk used a module-load-time
// constant). Feeds only the per-credit-card statistics thunks below, which keep
// their stale-by-design buster — a different domain from the `/statistics`
// family, which is refreshed by key invalidation and takes no buster.
const cacheTimestamp = (): string =>
  moment().format('dddd, Do MMM YYYY, h:mm:ss A')

type VerifyCreditCardVariables = {
  creditCardId: string
  body: VerifyCreditCardBody
}

type StartCreditCardVerificationVariables = {
  creditCardId: string
}

/**
 * Recover the exact `err.response.data` the legacy Redux FAILURE reducers
 * consume. `normalizeQueryError` stashes the raw AxiosError on the
 * non-enumerable `originalError`; the create/register reducers run
 * `parseBackendErrors(payload)` which needs the full backend shape
 * (`error` + `details`), so we hand the untouched backend payload back.
 * Falls back to the normalized `QueryError` for non-axios / response-less
 * failures (where the old thunk would have thrown reading `err.response.data`).
 */
const recoverBackendErrorPayload = (
  error: QueryError,
): BackendErrorPayload<string> => {
  const original = error.originalError
  if (axios.isAxiosError(original) && original.response?.data) {
    return original.response.data as BackendErrorPayload<string>
  }
  // Non-axios / response-less failure: hand back the normalized backend fields
  // (never the React Query-only `status` / `originalError`).
  return {
    error: error.error,
    errorCode: error.errorCode,
    details: error.details,
  }
}

/**
 * Pure `mutationOptions()` factories for the three credit-card write endpoints.
 * They own only the `mutationFn`; invalidation, Redux sync, toasts and
 * statistics live in the thin domain hooks below (React Query's
 * `useMutation` callbacks fire first and always fire).
 */
export const creditCardWriteMutations = {
  create: () =>
    mutationOptions<CreditCardWriteResponse, QueryError, CreateCreditCardBody>({
      mutationFn: (body) => postCreditCard(body),
    }),
  verify: () =>
    mutationOptions<
      CreditCardWriteResponse,
      QueryError,
      VerifyCreditCardVariables
    >({
      mutationFn: ({ creditCardId, body }) =>
        postVerifyCreditCard(creditCardId, body),
    }),
  startVerification: () =>
    mutationOptions<
      CreditCardWriteResponse,
      QueryError,
      StartCreditCardVerificationVariables
    >({
      mutationFn: ({ creditCardId }) =>
        postStartCreditCardVerification(creditCardId),
    }),
}

/**
 * Create a credit card (budget). Replaces the
 * `postCreditCardAndOpenToastAndCloseFlyout` thunk. Preserves the thunk's
 * Redux lifecycle dispatches (INIT/SUCCESS/FAILURE — still consumed by the
 * createBudget / registerAccount / budgetOverview / accountOverview /
 * data.creditCards / data.users reducers) plus its statistics refresh, modal
 * close and success toast, and adds a NARROW cache invalidation of the new
 * card's detail key (never the `['creditcards']` family root).
 */
export const useCreateCreditCard = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const users = useSelector(selectUserListModelsFromState)

  return useMutation<CreditCardWriteResponse, QueryError, CreateCreditCardBody>(
    {
      ...creditCardWriteMutations.create(),
      onMutate: () => {
        dispatch({ type: POST_CREDIT_CARD_INIT })
      },
      onSuccess: (data, body) => {
        const timestamp = cacheTimestamp()
        dispatch({ type: POST_CREDIT_CARD_SUCCESS, payload: data })
        dispatch(closeModal())
        dispatch(
          getCreditCardsStatisticsActiveBudgetCount(
            [data.creditCard.id],
            timestamp,
          ),
        )
        dispatch(
          getCreditCardsStatisticsBudgetCount([data.creditCard.id], timestamp),
        )
        dispatch(getCreditCardsStatisticsActiveVcnCount([data.creditCard.id]))
        dispatch(getCreditCardsStatisticsVcnCount([data.creditCard.id]))
        // Only mounted consumers refetch; the backend cache stays intact.
        invalidateCanIssueVirtualCardsStatistics(queryClient)
        // Only the affected card's detail key — card lists stay refreshed via the
        // existing Redux path, so we don't invalidate the `['creditcards']` root.
        queryClient.invalidateQueries({
          queryKey: creditCardsQueryKeys.detail(data.creditCard.id),
        })
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: (
              <CreateBudgetToastMessage
                creditCard={new CreditCardListModel(data.creditCard)}
                users={users}
                emails={(body.emails?.length ?? 0) < 2}
              />
            ),
          }),
        )
      },
      onError: (error) => {
        dispatch({
          type: POST_CREDIT_CARD_FAILURE,
          payload: recoverBackendErrorPayload(error),
        })
      },
    },
  )
}

/**
 * Verify a credit card with a code. Replaces the
 * `postCreditCardVerifyThenOpenToast` thunk and the legacy
 * `useVerifyCreditCard` (`useExtendMutation`) hook. Preserves the
 * verify Redux lifecycle (INIT/SUCCESS/FAILURE — read by AccountOverview via
 * accountOverview.reducer, and by data.creditCards), the non-blocked-only
 * statistics refresh + success toast, and the NARROW detail-key invalidation.
 */
export const useVerifyCreditCard = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  return useMutation<
    CreditCardWriteResponse,
    QueryError,
    VerifyCreditCardVariables
  >({
    ...creditCardWriteMutations.verify(),
    onMutate: () => {
      dispatch({ type: POST_CREDIT_CARD_VERIFY_INIT })
    },
    onSuccess: (data) => {
      if (data.creditCard.status !== CreditCardStatus.Blocked) {
        invalidateCanIssueVirtualCardsStatistics(queryClient)
        queryClient.invalidateQueries({
          queryKey: creditCardsQueryKeys.detail(data.creditCard.id),
        })
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: <CreateCreditCardVerificationSuccessMessage />,
          }),
        )
      }
      dispatch({ type: POST_CREDIT_CARD_VERIFY_SUCCESS, payload: data })
    },
    onError: (error) => {
      dispatch({
        type: POST_CREDIT_CARD_VERIFY_FAILURE,
        payload: recoverBackendErrorPayload(error),
      })
    },
  })
}

/**
 * (Re)send a credit-card verification code. Replaces the
 * `postCreditCardStartVerificationThenOpenToast` thunk and the legacy
 * `useStartCreditCardVerification` (`useExtendMutation`) hook. Preserves the
 * start-verification Redux lifecycle (INIT/SUCCESS/FAILURE — read by
 * AccountOverview) and the "new code sent" toast.
 */
export const useStartCreditCardVerification = () => {
  const dispatch = useDispatch()

  return useMutation<
    CreditCardWriteResponse,
    QueryError,
    StartCreditCardVerificationVariables
  >({
    ...creditCardWriteMutations.startVerification(),
    onMutate: () => {
      dispatch({ type: POST_CREDIT_CARD_START_VERIFICATION_INIT })
    },
    onSuccess: (data) => {
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: <NewVerificationCodeSentToastMessage />,
        }),
      )
      dispatch({
        type: POST_CREDIT_CARD_START_VERIFICATION_SUCCESS,
        payload: data,
      })
    },
    onError: (error) => {
      dispatch({
        type: POST_CREDIT_CARD_START_VERIFICATION_FAILURE,
        payload: recoverBackendErrorPayload(error),
      })
    },
  })
}
